import globalMessage from "@/utils/globalMessage";
import message from "@/utils/globalMessage";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Checkbox, Drawer, Empty, Form, Input, Spin, Upload } from "antd";
import {
  ArrowLeftOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  FileSearchOutlined,
  FilterOutlined,
  FolderAddOutlined,
  MoreOutlined,
  ReloadOutlined,
  ScissorOutlined,
  SearchOutlined,
  StarFilled,
  StarOutlined,
  TagsOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import Popconfirm from "@components/Feedback/Popconfirm";
import _ from "lodash";
import moment from "moment";
import { useSelector } from "react-redux";
import { useAuth, AUTH_LEVEL } from "@/hooks/use-auth";
import { api } from "@/redux/api";
import { jsforceResponse } from "@/config/httpService";
import TagPill from "@components/TagPill";
import { FileIcon } from "./Icon";
import { CategoryOptions, CategoryTag } from "./categoryTag";
import { FileViewer } from "./doc-viewer";
import { FormItemTag } from "./FormItemTag";
import { MoveToModal } from "./MoveToModal";
import ResponsiveModal from "@components/ResponsiveModal";
import {
  allowedFileTypes,
  beforeUpload,
  canOpenInApp,
  copyLink,
  FILE_MANAGEMENT_POPUP_Z_INDEX,
  formatFilesToTreeData,
  getFileExtension,
  getPathsByCurrentTree,
  getSafeUrl,
  handleDownload as downloadFile,
  openInApp,
  openInBrowser,
  usePreviewUrl,
} from "./utils";
import "./h5.css";
import Select from "@components/ResponsiveSelect";

const fileTypeOptions = [
  { value: "pdf", label: "PDF" },
  { value: "doc", label: "Word" },
  { value: "docx", label: "Word" },
  { value: "xls", label: "Excel" },
  { value: "xlsx", label: "Excel" },
  { value: "ppt", label: "PowerPoint" },
  { value: "pptx", label: "PowerPoint" },
  { value: "jpg", label: "Image (JPG)" },
  { value: "jpeg", label: "Image (JPEG)" },
  { value: "png", label: "Image (PNG)" },
  { value: "gif", label: "Image (GIF)" },
  { value: "bmp", label: "Image (BMP)" },
  { value: "webp", label: "Image (WebP)" },
  { value: "mp4", label: "Video (MP4)" },
  { value: "mov", label: "Video (MOV)" },
  { value: "avi", label: "Video (AVI)" },
  { value: "mp3", label: "Audio (MP3)" },
  { value: "wav", label: "Audio (WAV)" },
  { value: "txt", label: "Text" },
];

const checkFileType = (file) => {
  const extension = file?.name?.split(".").pop().toLowerCase();
  return allowedFileTypes.includes(extension);
};

const formatFileSize = (bytes) => {
  if (bytes == null || isNaN(bytes)) return "";
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const getItemMeta = (item) => {
  if (item.isFolder) {
    const count = item.nodes?.length ?? 0;
    return count === 0 ? "Empty folder" : `${count} item${count === 1 ? "" : "s"}`;
  }
  const parts = [];
  if (item.modified_date || item.created_date) {
    parts.push(moment(item.modified_date || item.created_date).format("MM/DD/YYYY"));
  }
  const size = formatFileSize(item.size);
  if (size) parts.push(size);
  return parts.join(" / ");
};

export const FileManagementH5 = (props) => {
  const facility = useSelector((state) => state.Facility.selected.Id);
  const userId = useSelector((state) => state.Auth.userId);
  const canWrite = useAuth("filemanage", AUTH_LEVEL.WRITE);
  const canFullAccess = useAuth("filemanage", AUTH_LEVEL.FULL_ACCESS);

  const [searchKeyword, setSearchKeyword] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentTreeKey, setCurrentTreeKey] = useState(null);
  const [treeData, setTreeData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [quickAccess, setQuickAccess] = useState(null);
  const [filterType, setFilterType] = useState(null);
  const [filterCategory, setFilterCategory] = useState(null);
  const [currentOpenFile, setCurrentOpenFile] = useState(null);
  const [actionRecord, setActionRecord] = useState(null);
  const [selectionMode, setSelectionMode] = useState(false);

  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [isEditTagOpen, setIsEditTagOpen] = useState(false);
  const [isMoveToOpen, setIsMoveToOpen] = useState(false);
  const [isFileOpen, setIsFileOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const [isReloadLoading, setIsReloadLoading] = useState(false);
  const [isCreatingFolderLoading, setIsCreatingFolderLoading] = useState(false);
  const [isUploadingFileLoading, setIsUploadingFileLoading] = useState(false);
  const [isRenamingLoading, setIsRenamingLoading] = useState(false);

  const [newFolderForm] = Form.useForm();
  const [uploadForm] = Form.useForm();
  const [renameForm] = Form.useForm();
  const [tagForm] = Form.useForm();
  const [moveForm] = Form.useForm();

  const currentFileRef = useRef(null);
  const rootRef = useRef(null);
  const originalFilesRef = useRef({ files: [], folders: [] });
  const [previewUrl, isFileViewLoading] = usePreviewUrl(currentOpenFile);

  const [favoriteIds, setFavoriteIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("fileFavorites") || "[]").map(String);
    } catch {
      return [];
    }
  });

  const isSearch = debouncedSearch.length > 0;
  const isOwner = (record) => record?.createdby === userId;
  const isFavorited = (id) => favoriteIds.includes(String(id));

  const debouncedSetSearch = useMemo(
    () => _.debounce((value) => setDebouncedSearch(value), 300),
    []
  );

  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const clearSelection = () => {
    setSelectedRowKeys([]);
    setSelectionMode(false);
  };

  const setCurrentTreeKeyWithClearSearch = useCallback((value) => {
    setCurrentTreeKey(value);
    setSearchKeyword("");
    setDebouncedSearch("");
    setSelectedRowKeys([]);
    setSelectionMode(false);
    setQuickAccess(null);
  }, []);

  const handleQuickAccess = (type) => {
    setQuickAccess(type === "all" ? null : type);
    setCurrentTreeKey(type === "all" ? rootRef.current : null);
    setSearchKeyword("");
    setDebouncedSearch("");
    clearSelection();
  };

  const handleGetData = useCallback(async () => {
    setIsReloadLoading(true);
    const savedPosition = quickAccess
      ? { type: "quickAccess", value: quickAccess }
      : { type: "folder", id: currentTreeKey?.id };
    const query =
      "page=1&page_size=9999&table_name=" +
      props.tableName +
      (props.recordId ? "&record_id=" + props.recordId : "");

    const [filesResult, foldersResult] = await Promise.all([
      api.getFiles(query),
      api.getFolders(query),
    ]);
    jsforceResponse(filesResult, "", false, true);
    jsforceResponse(foldersResult, "", false, true);

    if (filesResult.status === 200 && foldersResult.status === 200) {
      const [resultData, treeKey, originalFiles] = formatFilesToTreeData(
        _.get(filesResult, "data.results") ?? [],
        _.get(foldersResult, "data.results") ?? [],
        savedPosition.type === "folder" ? savedPosition.id : undefined,
        props
      );
      setTreeData([resultData]);
      rootRef.current = resultData;
      originalFilesRef.current = originalFiles;
      setSearchKeyword("");
      setDebouncedSearch("");
      setSelectedRowKeys([]);

      if (savedPosition.type === "quickAccess") {
        setCurrentTreeKey(null);
        setQuickAccess(savedPosition.value);
      } else {
        setCurrentTreeKey(treeKey || resultData);
        setQuickAccess(null);
      }
    }
    setIsFirstLoading(false);
    setIsReloadLoading(false);
  }, [currentTreeKey?.id, props, quickAccess]);

  useEffect(() => {
    handleGetData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    clearSelection();
    await handleGetData();
  };

  const currentFiles = useMemo(() => {
    let allItems;
    if (quickAccess === "recent") {
      allItems = [...originalFilesRef.current.files].sort(
        (a, b) =>
          new Date(b.modified_date || b.created_date || 0) -
          new Date(a.modified_date || a.created_date || 0)
      );
    } else if (quickAccess === "favorites") {
      allItems = []
        .concat(originalFilesRef.current.folders, originalFilesRef.current.files)
        .filter((item) => favoriteIds.includes(String(item.id)));
    } else if (isSearch) {
      allItems = [].concat(originalFilesRef.current.folders, originalFilesRef.current.files);
    } else {
      allItems = currentTreeKey?.nodes ?? [];
    }

    let filtered = isSearch
      ? allItems.filter((item) =>
          (item.name || item.title || "").toLowerCase().includes(debouncedSearch.toLowerCase())
        )
      : allItems;

    if (filterType) {
      filtered = filtered.filter(
        (item) => !item.isFolder && getFileExtension(item.name || item.title) === filterType
      );
    }

    if (filterCategory) {
      filtered = filtered.filter((item) => !item.isFolder && item.category === filterCategory);
    }

    return filtered;
  }, [currentTreeKey, debouncedSearch, favoriteIds, filterCategory, filterType, isSearch, quickAccess]);

  const currentPath = useMemo(() => {
    if (quickAccess === "recent") return "Recent";
    if (quickAccess === "favorites") return "Favorites";
    if (isSearch) return "Search Results";
    const path = getPathsByCurrentTree(currentTreeKey).map((item) => item.name || item.title);
    return path.length > 0 ? path.join(" / ") : "root";
  }, [currentTreeKey, isSearch, quickAccess]);

  const canCreateFolder = canWrite && (currentTreeKey?.isSystem ? currentTreeKey?.context?.canCreate : true);
  const canUploadFile = canWrite && (currentTreeKey?.isSystem ? currentTreeKey?.context?.canUpload : true);
  const canDeleteSelected =
    selectedRowKeys.length > 0 &&
    selectedRowKeys.every((item) => !item.isSystem) &&
    (canFullAccess || selectedRowKeys.every((item) => isOwner(item)));

  const openRenameModal = (record) => {
    currentFileRef.current = record;
    renameForm.setFieldsValue({ name: record.name });
    setIsRenameOpen(true);
  };

  const openEditTagModal = (record) => {
    currentFileRef.current = record;
    tagForm.setFieldsValue({ tags: record.tags_list });
    setIsEditTagOpen(true);
  };

  const openMoveToModal = (record) => {
    currentFileRef.current = record;
    moveForm.setFieldsValue({ folder: record.folder });
    setIsMoveToOpen(true);
  };

  const toggleFavorite = (id) => {
    const key = String(id);
    setFavoriteIds((prev) => {
      const next = prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key];
      localStorage.setItem("fileFavorites", JSON.stringify(next));
      message.success(prev.includes(key) ? "Removed from Favorites" : "Added to Favorites");
      return next;
    });
  };

  const handleCreateFolder = async () => {
    const { name } = await newFolderForm.validateFields().catch(() => ({}));
    if (!name || !currentTreeKey) return;

    setIsCreatingFolderLoading(true);
    try {
      let parent = currentTreeKey?.id;
      let equipment = props.equipmentId;
      let table_name = props.tableName;
      let record_id = props.recordId;

      if (currentTreeKey.isSystem) {
        parent = null;
        equipment = currentTreeKey.context.equipmentId;
        table_name = currentTreeKey.context.table_name;
        record_id = currentTreeKey.context.record_id;
      } else if (props.tableName === "All") {
        table_name = currentTreeKey.table_name;
        record_id = currentTreeKey.record_id;
        equipment = currentTreeKey.equipment;
      }

      const result = await api.createFolder({
        name,
        table_name,
        ...(record_id ? { record_id } : {}),
        parent,
        facility,
        equipment,
      });

      if (result.status === 201) {
        globalMessage.success({ message: "Folder created successfully", description: "" });
      } else {
        globalMessage.error({
          message: "Failed to create folder",
          description: result.data?.message || "",
        });
      }
    } catch (error) {
      globalMessage.error({
        message: "Failed to create folder",
        description: error.message || "",
      });
    }
    await refresh();
    setNewFolderOpen(false);
    setIsCreatingFolderLoading(false);
  };

  const handleUpload = async () => {
    const values = await uploadForm.validateFields().catch(() => ({ isErr: true }));
    if (values.isErr || !currentTreeKey) return;

    const { tags, category, description, files = [] } = values;
    if (files.length === 0) {
      globalMessage.error({ message: "Please upload a file", description: "" });
      return;
    }

    let record_id;
    let equipment;
    let table_name;

    if (props.tableName === "All") {
      if (currentTreeKey.isSystem) {
        record_id = currentTreeKey.context.record_id;
        table_name = currentTreeKey.context.table_name;
        equipment = currentTreeKey.context.equipmentId;
      } else {
        record_id = currentTreeKey.record_id;
        table_name = currentTreeKey.table_name;
        equipment = currentTreeKey.equipment;
      }
    } else {
      record_id = props.recordId;
      table_name = props.tableName;
      equipment = props.equipmentId;
    }

    const body = {
      table_name,
      record_id,
      facility,
      files,
      category,
      description,
      tags,
      equipment,
    };

    if (!record_id) delete body.record_id;
    if (currentTreeKey?.isSystem) {
      body.folder = null;
    } else if (currentTreeKey?.id) {
      body.folder = currentTreeKey.id;
    }

    setIsUploadingFileLoading(true);
    const result = await api.uploadFile(body);
    jsforceResponse(result, "", false, true);
    if (result.status >= 200 && result.status < 300) {
      await handleGetData();
      setIsUploadOpen(false);
      uploadForm.resetFields();
    }
    setIsUploadingFileLoading(false);
  };

  const deleteRecords = async (records) => {
    await Promise.all(
      records.map(({ id, isFolder, record_id, table_name }) => {
        const fetch = isFolder ? api.deleteFolder : api.deleteFile;
        return fetch(id, {
          ...(record_id ? { record_id } : {}),
          table_name,
        });
      })
    );
    message.success("Deleted successfully");
    setIsDeleteOpen(false);
    setActionRecord(null);
    await refresh();
  };

  const handleRename = async () => {
    const currentFile = currentFileRef.current;
    if (!currentFile) return;
    const { name } = await renameForm.validateFields().catch(() => ({}));
    if (!name) return;

    setIsRenamingLoading(true);
    const { id, record_id, table_name, type, description, category, tags } = currentFile;
    const fetch = currentFile.isFolder ? api.modifyFolder : api.modifyFile;
    const res = await fetch(id, {
      name,
      ...(record_id ? { record_id } : {}),
      table_name,
      type,
      description,
      category,
      tags,
      ...(currentFile.isFolder ? { facility } : {}),
    }).catch(() => ({}));

    if (res.status === 200) {
      await refresh();
      setIsRenameOpen(false);
    } else {
      message.error("error");
    }
    setIsRenamingLoading(false);
  };

  const handleEditTag = async () => {
    const currentFile = currentFileRef.current;
    if (!currentFile) return;
    const { tags } = await tagForm.validateFields().catch(() => ({}));

    setIsRenamingLoading(true);
    const fetch = currentFile.isFolder ? api.modifyFolder : api.modifyFile;
    const { id, record_id, table_name, type, description, category, name } = currentFile;
    const res = await fetch(id, {
      name,
      record_id,
      table_name,
      type,
      description,
      category,
      tags,
    }).catch(() => ({}));

    if (res.status === 200) {
      await refresh();
      setIsEditTagOpen(false);
    } else {
      message.error("error");
    }
    setIsRenamingLoading(false);
  };

  const handleMoveTo = async () => {
    const currentFile = currentFileRef.current;
    if (!currentFile) return;
    const formData = await moveForm.validateFields().catch(() => {
      message.error("Please select a folder");
      return null;
    });
    if (!formData?.folder) return;

    const selectedFolder =
      formData.folder === "root"
        ? rootRef.current
        : originalFilesRef.current.folders.find((item) => item.id === formData.folder);
    if (!selectedFolder) return;

    const { id, type, description, category, tags, name } = currentFile;
    const record_id = selectedFolder.isSystem ? selectedFolder.context?.record_id : currentFile.record_id;
    const table_name = selectedFolder.isSystem ? selectedFolder.context?.table_name : currentFile.table_name;
    const equipment = selectedFolder.isSystem ? selectedFolder.context?.equipmentId : currentFile.equipment;
    const fetch = currentFile.isFolder ? api.modifyFolder : api.modifyFile;
    const body = {
      name,
      record_id,
      table_name,
      equipment,
      type,
      description,
      category,
      tags,
      folder: selectedFolder.isSystem ? null : selectedFolder.id,
    };
    if (!body.record_id) delete body.record_id;

    const res = await fetch(id, body, true).catch(() => ({}));
    if (res.status === 200) {
      await refresh();
      setIsMoveToOpen(false);
    } else {
      message.error("error");
    }
  };

  const handleItemClick = (item) => {
    if (selectionMode) {
      setSelectedRowKeys((prev) =>
        prev.find((selected) => selected.key === item.key)
          ? prev.filter((selected) => selected.key !== item.key)
          : [...prev, item]
      );
      return;
    }

    if (item.isFolder) {
      setCurrentTreeKeyWithClearSearch(item);
    } else {
      setActionRecord(item);
    }
  };

  const handlePreview = (record) => {
    setCurrentOpenFile(record);
    setIsFileOpen(true);
    setActionRecord(null);
  };

  const handleGoBack = () => {
    if (quickAccess || isSearch) {
      handleQuickAccess("all");
      return;
    }
    if (currentTreeKey?.parentFolder) {
      setCurrentTreeKeyWithClearSearch(currentTreeKey.parentFolder);
    }
  };

  const selectedFileRecords = selectedRowKeys.filter((item) => !item.isFolder);

  return (
    <div className="file-management-h5">
      <div className="fm-h5-header">
        <Button
          type="text"
          className="fm-h5-icon-button"
          icon={<ArrowLeftOutlined />}
          disabled={!quickAccess && !isSearch && !currentTreeKey?.parentFolder}
          onClick={handleGoBack}
        />
        <div className="fm-h5-title-wrap">
          <div className="fm-h5-title">{currentTreeKey?.name || currentTreeKey?.title || currentPath}</div>
          <div className="fm-h5-path">{currentPath}</div>
        </div>
        <Button type="text" className="fm-h5-icon-button" icon={<SearchOutlined />} onClick={() => setIsSearchOpen(true)} />
        <Button type="text" className="fm-h5-icon-button" icon={<FilterOutlined />} onClick={() => setIsFilterOpen(true)} />
      </div>

      <div className="fm-h5-quick-access">
        {[
          { key: "all", label: "All Files", icon: <FileSearchOutlined /> },
          { key: "recent", label: "Recent", icon: <ClockCircleOutlined /> },
          { key: "favorites", label: "Favorites", icon: <StarOutlined /> },
        ].map((item) => (
          <button
            key={item.key}
            type="button"
            className={`fm-h5-chip${(item.key === "all" ? !quickAccess : quickAccess === item.key) ? " fm-h5-chip-active" : ""}`}
            onClick={() => handleQuickAccess(item.key)}
          >
            {item.icon}
            <span>{item.label}</span>
          </button>
        ))}
      </div>

      <div className="fm-h5-toolbar">
        <div className="fm-h5-toolbar-left">
          {canCreateFolder && (
            <Button
              size="small"
              icon={<FolderAddOutlined />}
              disabled={isSearch || quickAccess}
              onClick={() => {
                newFolderForm.setFieldsValue({
                  name: "New Folder",
                  path: ["root"].concat(getPathsByCurrentTree(currentTreeKey).map((item) => item.name)).join("/"),
                });
                setNewFolderOpen(true);
              }}
            >
              Folder
            </Button>
          )}
          {canUploadFile && (
            <Button size="small" icon={<UploadOutlined />} disabled={isSearch || quickAccess} onClick={() => setIsUploadOpen(true)}>
              Upload
            </Button>
          )}
          <Button size="small" icon={<ReloadOutlined />} loading={isReloadLoading} onClick={refresh}>
            Refresh
          </Button>
        </div>
        <Button
          size="small"
          type={selectionMode ? "primary" : "default"}
          onClick={() => {
            setSelectionMode((prev) => !prev);
            setSelectedRowKeys([]);
          }}
        >
          Select
        </Button>
      </div>

      {(isSearch || filterType || filterCategory) && (
        <div className="fm-h5-active-filter">
          {isSearch ? <span>Search: {debouncedSearch}</span> : null}
          {filterType ? <span>Type: {filterType}</span> : null}
          {filterCategory ? <span>Category: {filterCategory}</span> : null}
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={() => {
              setSearchKeyword("");
              setDebouncedSearch("");
              setFilterType(null);
              setFilterCategory(null);
            }}
          />
        </div>
      )}

      <Spin spinning={isFirstLoading || isReloadLoading}>
        <div className="fm-h5-list">
          {currentFiles.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={isSearch ? "No files found." : "This folder is empty."} />
          ) : (
            currentFiles.map((item) => {
              const selected = selectedRowKeys.some((selectedItem) => selectedItem.key === item.key);
              const favorited = isFavorited(item.id);
              return (
                <div
                  key={item.key}
                  className={`fm-h5-item${selected ? " fm-h5-item-selected" : ""}`}
                  onClick={() => handleItemClick(item)}
                >
                  {selectionMode && (
                    <Checkbox
                      checked={selected}
                      className="fm-h5-select"
                      onClick={(event) => event.stopPropagation()}
                      onChange={() => handleItemClick(item)}
                    />
                  )}
                  <div className="fm-h5-item-icon">
                    <FileIcon isFolder={item.isFolder} title={item.title || item.name} />
                  </div>
                  <div className="fm-h5-item-main">
                    <div className="fm-h5-item-name">
                      <span>{item.name || item.title}</span>
                      {favorited && <StarFilled className="fm-h5-favorite" />}
                    </div>
                    <div className="fm-h5-item-meta">{getItemMeta(item)}</div>
                    {!item.isFolder && (
                      <div className="fm-h5-item-tags">
                        {item.category ? <CategoryTag type={item.category} /> : null}
                        {Array.isArray(item.tags_list) &&
                          item.tags_list.slice(0, 2).map((tag) => <TagPill key={tag} name={tag} size="sm" />)}
                      </div>
                    )}
                  </div>
                  {!selectionMode && (
                    <Button
                      type="text"
                      className="fm-h5-more"
                      icon={<MoreOutlined />}
                      onClick={(event) => {
                        event.stopPropagation();
                        setActionRecord(item);
                      }}
                    />
                  )}
                </div>
              );
            })
          )}
        </div>
      </Spin>

      {selectionMode && selectedRowKeys.length > 0 && (
        <div className="fm-h5-selection-bar">
          <span>{selectedRowKeys.length} selected</span>
          {selectedFileRecords.length > 0 && (
            <Button size="small" icon={<DownloadOutlined />} onClick={() => selectedFileRecords.forEach((record) => downloadFile(record))}>
              Download
            </Button>
          )}
          {canDeleteSelected && (
            <Popconfirm
              title="Do you want to delete these records?"
              overlayStyle={{ zIndex: FILE_MANAGEMENT_POPUP_Z_INDEX }}
              onConfirm={() => deleteRecords(selectedRowKeys)}
            >
              <Button size="small" danger icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          )}
        </div>
      )}

      <Drawer
        placement="bottom"
        open={Boolean(actionRecord)}
        onClose={() => setActionRecord(null)}
        height="auto"
        className="fm-h5-action-drawer"
        title={actionRecord?.name || actionRecord?.title}
      >
        {actionRecord && (
          <div className="fm-h5-actions">
            {actionRecord.isFolder ? (
              <button type="button" onClick={() => setCurrentTreeKeyWithClearSearch(actionRecord)}>
                <FileSearchOutlined /> Open Folder
              </button>
            ) : (
              <button type="button" onClick={() => handlePreview(actionRecord)}>
                <EyeOutlined /> Preview
              </button>
            )}
            {!actionRecord.isFolder && canOpenInApp(actionRecord) && (
              <>
                <button type="button" onClick={() => openInBrowser(actionRecord)}>
                  <FileSearchOutlined /> Open in Browser
                </button>
                <button type="button" onClick={() => openInApp(actionRecord)}>
                  <FileSearchOutlined /> Open in App
                </button>
              </>
            )}
            {!actionRecord.isFolder && (
              <button type="button" onClick={() => downloadFile(actionRecord)}>
                <DownloadOutlined /> Download
              </button>
            )}
            {!actionRecord.isFolder && (
              <button type="button" onClick={() => copyLink(actionRecord)}>
                <CopyOutlined /> Copy Link
              </button>
            )}
            {!actionRecord.isSystem && canWrite && (
              <button type="button" onClick={() => openRenameModal(actionRecord)}>
                <EditOutlined /> Rename
              </button>
            )}
            {!actionRecord.isFolder && canWrite && (
              <button type="button" onClick={() => openEditTagModal(actionRecord)}>
                <TagsOutlined /> Edit Tags
              </button>
            )}
            {!actionRecord.isSystem && canWrite && (
              <button type="button" onClick={() => openMoveToModal(actionRecord)}>
                <ScissorOutlined /> Move To
              </button>
            )}
            <button type="button" onClick={() => toggleFavorite(actionRecord.id)}>
              {isFavorited(actionRecord.id) ? <StarFilled /> : <StarOutlined />}
              {isFavorited(actionRecord.id) ? "Remove from Favorites" : "Add to Favorites"}
            </button>
            {!actionRecord.isSystem && (canFullAccess || isOwner(actionRecord)) && (
              <button type="button" className="fm-h5-action-danger" onClick={() => setIsDeleteOpen(true)}>
                <DeleteOutlined /> Delete
              </button>
            )}
          </div>
        )}
      </Drawer>

      <Drawer
        title="Search"
        placement="top"
        height={150}
        open={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        className="fm-h5-search-drawer"
      >
        <Input
          autoFocus
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search files..."
          value={searchKeyword}
          onChange={(event) => {
            setSearchKeyword(event.target.value);
            debouncedSetSearch(event.target.value);
          }}
        />
      </Drawer>

      <Drawer
        title="Filters"
        placement="bottom"
        height={260}
        open={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        className="fm-h5-filter-drawer"
      >
        <div className="fm-h5-filter-form">
          <Select allowClear placeholder="File type" value={filterType} onChange={setFilterType} options={fileTypeOptions} />
          <Select allowClear placeholder="Category" value={filterCategory} onChange={setFilterCategory} options={CategoryOptions} />
          <Button
            onClick={() => {
              setFilterType(null);
              setFilterCategory(null);
            }}
          >
            Clear Filters
          </Button>
        </div>
      </Drawer>

      <ResponsiveModal
        title="New Folder"
        open={newFolderOpen}
        onOk={handleCreateFolder}
        onCancel={() => setNewFolderOpen(false)}
        destroyOnClose
        okButtonProps={{ loading: isCreatingFolderLoading, disabled: isCreatingFolderLoading }}
      >
        <Form form={newFolderForm} autoComplete="off">
          <Form.Item
            label="Name"
            name="name"
            rules={[
              { required: true, message: "Please input folder name" },
              {
                validator: (_, value) => {
                  if (currentFiles.some((item) => item.name === value)) {
                    return Promise.reject("This folder name is already in use.");
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="Folder Name" />
          </Form.Item>
          <Form.Item label="Path" name="path">
            <Input disabled />
          </Form.Item>
        </Form>
      </ResponsiveModal>

      <Drawer
        open={isUploadOpen}
        title="Upload a File"
        placement="bottom"
        height="78vh"
        className="fm-h5-upload-drawer"
        onClose={() => {
          setIsUploadOpen(false);
          uploadForm.resetFields();
        }}
        destroyOnClose
      >
        <div className="fm-h5-upload-content">
          <Form form={uploadForm} layout="vertical" autoComplete="off">
            <Form.Item label="Tags" name="tags">
              <FormItemTag record={currentTreeKey} />
            </Form.Item>
            <Form.Item label="category" name="category">
              <Select options={CategoryOptions} />
            </Form.Item>
            <Form.Item
              name="files"
              label="Upload File"
              valuePropName="fileList"
              getValueFromEvent={(event) => (Array.isArray(event) ? event : event && event.fileList)}
              rules={[
                {
                  validator(_, files = []) {
                    let validName;
                    const isDuplicate = currentFiles.some((current) =>
                      files.some((file) => {
                        if (current.name === file.name) {
                          validName = current.name;
                          return true;
                        }
                        return false;
                      })
                    );
                    if (isDuplicate) {
                      return Promise.reject(`File name ${validName} is already in use.`);
                    }
                    for (const file of files) {
                      if (!checkFileType(file)) {
                        return Promise.reject(`The file \`${file.name}\` is not allowed to upload.please check the file extension.`);
                      }
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Upload.Dragger multiple beforeUpload={beforeUpload}>
                <p className="ant-upload-drag-icon">
                  <UploadOutlined />
                </p>
                <p className="ant-upload-text">Tap or drag file to this area to upload</p>
              </Upload.Dragger>
            </Form.Item>
            <Form.Item label="description" name="description">
              <Input />
            </Form.Item>
          </Form>
        </div>
        <div className="fm-h5-drawer-footer">
          <Button
            onClick={() => {
              setIsUploadOpen(false);
              uploadForm.resetFields();
            }}
          >
            Cancel
          </Button>
          <Button type="primary" loading={isUploadingFileLoading} disabled={isUploadingFileLoading} onClick={handleUpload}>
            Upload
          </Button>
        </div>
      </Drawer>

      <ResponsiveModal
        title="Rename"
        open={isRenameOpen}
        onCancel={() => setIsRenameOpen(false)}
        onOk={handleRename}
        okButtonProps={{ loading: isRenamingLoading, disabled: isRenamingLoading }}
        destroyOnClose
      >
        <Form form={renameForm} layout="vertical" autoComplete="off">
          <Form.Item
            label="Name"
            name="name"
            rules={[
              { required: true },
              {
                validator(_, value) {
                  if (value === currentFileRef.current?.name) return Promise.resolve();
                  if (currentFiles.some((item) => item.name === value)) {
                    return Promise.reject("This file name is already in use.");
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="New Name" />
          </Form.Item>
        </Form>
      </ResponsiveModal>

      <ResponsiveModal
        title="Edit Tag"
        open={isEditTagOpen}
        onCancel={() => setIsEditTagOpen(false)}
        onOk={handleEditTag}
        okButtonProps={{ loading: isRenamingLoading, disabled: isRenamingLoading }}
        destroyOnClose
      >
        <Form form={tagForm} layout="vertical" autoComplete="off">
          <Form.Item label="Tags" name="tags">
            <FormItemTag record={currentFileRef.current} />
          </Form.Item>
        </Form>
      </ResponsiveModal>

      <MoveToModal treeData={treeData} open={isMoveToOpen} setOpen={setIsMoveToOpen} moveForm={moveForm} handleMoveTo={handleMoveTo} />

      <Drawer
        title={currentOpenFile?.title || currentOpenFile?.name || "view file"}
        open={isFileOpen}
        onClose={() => setIsFileOpen(false)}
        destroyOnClose
        width="100%"
        height="92vh"
        placement="bottom"
        className="fm-h5-preview-drawer"
      >
        <Spin spinning={isFileViewLoading}>
          <FileViewer url={previewUrl} currentOpenFile={currentOpenFile} />
        </Spin>
      </Drawer>

      <ResponsiveModal
        title="Delete"
        open={isDeleteOpen}
        onCancel={() => setIsDeleteOpen(false)}
        onOk={() => actionRecord && deleteRecords([actionRecord])}
        okButtonProps={{ danger: true }}
        destroyOnClose
      >
        Do you want to delete this record?
      </ResponsiveModal>
    </div>
  );
};

export default FileManagementH5;
